<template>
    <div
        :class="classObject"
    >
        <div
            class="z-chart__el"
            ref="chart"
        ></div>
        <div
            class="z-chart__legend"
            ref="legend"
        ></div>
    </div>
</template>

<script setup>
import { bb } from 'billboard.js/dist/billboard.pkgd'
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {useGetter} from '@/composable/useGetter'

const props = defineProps({
    options: {
        type: Object,
        required: true
    },
    media: {
        type: Object
    },
    legend: {
        type: Object,
        default: () => {
            return {
                position: 'left-bottom'
            }
        }
    },
    theme: {
        type: String
    }
})


const chart = ref(null)
const legend = ref(null)
const $chart = ref(null)

const { lang } = useGetter(['lang'])
const localizeValues = (value) => {
    return lang.value === 'ru' ? value.toString().replace('.', ',') : value
}

const themeChart = {
    grid: {
        y: {
            show: true
        }
    },
    axis: {
        x: {
            tick: {
                show: false
            },
            label: {
                color: 'var( --color-black)'
            }
        },
        y: {
            tick: {
                show: false
            }
        }
    },
    padding: {
        top: 0,
        right: 0,
        bottom: 0,
        left: 30
    },
    color: {
        pattern: [
            'var(--color-graph-gr-1)',
            'var(--color-graph-gr-2)',
            'var(--color-graph-gr-4)',
            'var(--color-graph-gr-3)',
            'var(--color-graph-gr-5)',
            'var(--color-graph-gr-6)',
            'var(--color-graph-gr-7)',
            'var(--color-graph-gr-8)',
            'var(--color-graph-gr-9)',
        ]
    },
    legend: {
        contents: {
            template: `<span class="z-chart__legend-item">
                                <span class="z-chart__legend-square" style='background-color:{=COLOR}'"></span>
                                <span class="z-chart__legend-title">{=TITLE}</span>
                            </span>`
        }
    },
    bar: {
        width: {
            ratio: 0.7,
            max: 50
        }
    },
    labels: {
        format: value => localizeValues(value)
    },
    tooltip: {
        format: {
            value: value => localizeValues(value)
        }
    }
}

const generateChart = () => {
    /**
     * @function generateChart
     *
     * @description
     * generates the charts bases on the options this
     *
     * @returns {Object} the generated chart this
     */
    const options = Object.assign({
        ...themeChart,
        bindto: chart.value
    }, props.options)

    if (options.legend.contents) {
        options.legend.contents.bindto = legend.value
    }

    return bb.generate(options)
}

const loadDataChart = () => {
    /**
     * @function loadDataChart
     *
     * @description
     * Updates the chart with the new data
     *
     * @param {object} options from this to update the chart with
     */
    return data => {
        if (!$chart.value) {
            $chart.value = generateChart(props.options)
        }
        $chart.value.load(data)
    }
}

const resizeChart = () => {
    if (props.media && window.innerWidth < props.media.point) {
        $chart.value.internal.config = Object.assign($chart.value.internal.config, props.media.options)
    }
}

const destroyChart = () => {
    /**
     * @function destroyChart
     *
     * @description
     * destroys the chart and sets ref to null
     */
    return () => {
        try {
            $chart.value.destroy()
            $chart.value = null
        } catch (error) {
            console.error('Internal Billboard.js error', error)
        }
    }
}

const classObject = computed(() => {
    let arrClass = [
        'z-chart'
    ]

    if (props.legend.position) {
        arrClass.push(`z-chart--legend-position-${props.legend.position}`)
    }

    if (props.theme) {
        arrClass.push(`z-chart--theme-${props.theme}`)
    }

    return arrClass
})

onMounted(() => {
    $chart.value = generateChart()

    window.addEventListener('resize', () => {
        resizeChart()
    })
})

onUnmounted(() => {
    destroyChart()
})

watch('props.options.data', loadDataChart, {deep: true})

</script>

<style lang="scss" src="./index.scss"></style>
